import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, map, of, switchMap, withLatestFrom } from 'rxjs';
import { TcsCustomPermission } from 'src/app/shared/authorization/permissions';
import { EntityType, UserRole } from 'src/graphql/generated/graphql.generated';
import { UserViewModel } from 'src/models/person-view-model';
import { AppState } from 'src/store/appstate';
import { selectCompanyByCompanyCode } from 'src/store/entityStores/company-selectors';
import { AccessibleItemProps } from 'src/store/generated/accessible-item-record.generated';
import { selectAllCommunicationCenterEntities } from 'src/store/generated/communication-center-record.generated';
import { Set } from 'immutable';
import { PermissionsService } from '../amg-authorization/permissions.service';
import { UserService } from '../tcs-identity/user.service';
import { AccessibleItemAuthorizationService } from './accessible-item-authorization.service';
import { CommCenterAuthorizationService } from './comm-center-authorization.service';

@Injectable({ providedIn: 'root' })
export class OrgAuthorizationService {
  constructor(
    private _commCenterAuthz: CommCenterAuthorizationService,
    private _user: UserService,
    private _store: Store<AppState>,
    private _permissions: PermissionsService,
    private _accessibleItems: AccessibleItemAuthorizationService,
  ) {}

  public isAuthorizedForCompanyCode(companyCode: string) {
    return this._store
      .select(selectCompanyByCompanyCode(companyCode))
      .pipe(switchMap((c) => (c != null ? this.isAuthorizedForCompanyId(c.id) : of(false))));
  }

  public isAuthorizedForCompanyId(companyId: string) {
    const hasGlobalRoleWithManageOrgAccess$ = this._user.hasAccessibleCompany(companyId).pipe(
      withLatestFrom(this._user.hasPermissionAnyGlobalRole(TcsCustomPermission.ManageOrg)),
      map(
        ([hasCompanyAccess, hasGlobalRoleOrgAccess]) => hasCompanyAccess && hasGlobalRoleOrgAccess,
      ),
    );

    const hasManageOrgAndCommCenterAccess$ = this._user
      .hasPermissionAnyScope(TcsCustomPermission.ManageOrg)
      .pipe(
        withLatestFrom(this._commCenterAuthz.isAuthorizedForCompany(companyId, false)),
        map(([hasScope, hasCommCenterAccess]) => hasScope && hasCommCenterAccess),
      );

    const hasScopedRoleWithManageOrgAccess$ = this._user
      .hasPermissionAnyScope(TcsCustomPermission.ManageOrg)
      .pipe(
        withLatestFrom(this._isAuthorizedForCompany(companyId)),
        map(([hasScope, hasCommCenterAccess]) => hasScope && hasCommCenterAccess),
      );

    return combineLatest([
      hasGlobalRoleWithManageOrgAccess$,
      hasManageOrgAndCommCenterAccess$,
      hasScopedRoleWithManageOrgAccess$,
    ]).pipe(
      map(
        ([
          hasGlobalRoleWithManageOrgAccess,
          hasManageOrgAndCommCenterAccess,
          hasScopedRoleWithManageOrgAccess,
        ]) => {
          // If a user has ManageOrg permission for any global roles (user.userRoles) and access to the company OR
          // If a user has ManageOrg permission for any scope (user.userRolesAllScopes) and access to a comm center within the company
          // then they are authorized
          return (
            hasManageOrgAndCommCenterAccess ||
            hasGlobalRoleWithManageOrgAccess ||
            hasScopedRoleWithManageOrgAccess
          );
        },
      ),
      distinctUntilChanged(),
    );
  }

  private _isAuthorizedForCompany(companyId: string, commCenterId?: string) {
    const commCenters$ = this._store
      .select(selectAllCommunicationCenterEntities)
      .pipe(
        map((commCenters) =>
          commCenters.filter((c) => companyId == null || companyId === c.companyId).valueSeq(),
        ),
      );

    const accessibleItems$ = this._accessibleItems.getAccessibleItems();
    return combineLatest([this._user.user$, accessibleItems$, commCenters$]).pipe(
      map(([user, accessibleItems, commCenters]) => {
        if (commCenters.count() > 0) {
          const authorizedForCompany = commCenters.filter((cc) => {
            return this._checkAuthorizedForCompany(
              cc.companyId,
              user,
              accessibleItems,
              cc.id,
              TcsCustomPermission.ManageOrg,
            );
          });
          return authorizedForCompany.count() > 0;
        } else {
          return this._checkAuthorizedForCompany(
            companyId,
            user,
            accessibleItems,
            commCenterId,
            TcsCustomPermission.ManageOrg,
          );
        }
      }),
    );
  }

  private _checkAuthorizedForCompany(
    companyId: string,
    user: UserViewModel | undefined,
    accessibleItems: AccessibleItemProps[],
    commCenterId?: string,
    permission?: TcsCustomPermission,
  ) {
    if (user == null) {
      return false;
    }

    if (this._checkUserAllAccessiblePermissions(user, accessibleItems, companyId, permission)) {
      return true;
    }

    const commCenterAi = accessibleItems.find(
      (ai) => ai.entityId === commCenterId && ai.type === EntityType.CommunicationCenter,
    );
    if (this._hasScopedPermission(commCenterAi, permission)) {
      // User has been granted access to this specific comm center
      return true;
    }

    return false;
  }

  private _checkUserAllAccessiblePermissions(
    user: UserViewModel,
    accessibleItems: AccessibleItemProps[],
    companyId?: string,
    permission?: TcsCustomPermission,
  ) {
    if (this._hasScopedPermission(user, permission)) {
      return true;
    }
    if (companyId != null) {
      const companyAi = accessibleItems.find(
        (ai) => ai.entityId === companyId && ai.type === EntityType.Company,
      );
      if (companyAi?.userRoles != null && this._hasScopedPermission(companyAi, permission)) {
        // User has been granted access to this comm center because
        // they have accesss to all comm centers in this company
        return true;
      }
    }
    return false;
  }

  private _hasScopedPermission(
    scope:
      | {
          userRoles: Set<UserRole> | null;
        }
      | undefined,
    permission?: TcsCustomPermission,
  ) {
    const permissionToCheck = permission ?? TcsCustomPermission.ManageOrg;
    if (scope == null) {
      return false;
    }
    const { userRoles } = scope;
    if (userRoles == null) {
      return false;
    }
    return this._permissions.hasPermission(userRoles.toArray(), permissionToCheck);
  }
}
